/**
 * ExerciseNameAutocomplete — input del nombre del ejercicio en el editor
 * con búsqueda en vivo contra el catálogo.
 *
 * Mientras el coach escribe, una lista flotante muestra hasta 8 ejercicios
 * que matchean (en `name` español o `name_en` inglés — así "fly" encuentra
 * "Aperturas con mancuerna"). Click en un item rellena nombre + libId +
 * gifUrl + músculo. Si el coach prefiere escribirlo libre, el draft se
 * confirma en blur.
 *
 * Cuando el ejercicio NO está en el catálogo (libId ausente), el coach puede
 * seguir tipeando un nombre custom y confirmar — el row queda sin gif y
 * marcado como custom. El botón 📚 al lado del campo abre el picker grande
 * con filtros si se necesita más control.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalog } from "../../../contexts/CatalogContext";
import { exerciseGifUrl, type Exercise } from "../../../api/exercises";
import { fuzzyMatch, normalize } from "../../../lib/normalizeText";

interface Props {
  value: string;
  onCommitText: (text: string) => void;
  onPick: (ex: Exercise) => void;
  placeholder?: string;
}

const MIN_QUERY = 2;
const MAX_RESULTS = 8;

export function ExerciseNameAutocomplete({ value, onCommitText, onPick, placeholder }: Props) {
  const { exercises, loaded, ensureLoaded } = useCatalog();
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const matches = useMemo<Exercise[]>(() => {
    if (!loaded) return [];
    const q = (draft || "").trim();
    const qNorm = normalize(q);
    // No mostramos sugerencias si el texto es muy corto o si ya es exactamente
    // el valor commiteado (caso típico: foco sin editar).
    if (!qNorm || qNorm.length < MIN_QUERY) return [];
    if (qNorm === normalize(value)) return [];
    // Mismo patrón que el ExercisePickerModal: fuzzyMatch tolerante a plural
    // y orden de palabras (sino "elevaciones laterales" no encontraba
    // "Elevación Lateral con Mancuerna").
    const out: Exercise[] = [];
    for (const ex of exercises) {
      const hay = `${ex.name} ${ex.name_en || ""}`;
      if (fuzzyMatch(q, hay)) {
        out.push(ex);
        if (out.length >= MAX_RESULTS) break;
      }
    }
    return out;
  }, [exercises, loaded, draft, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    setHi(0);
  }, [matches.length]);

  const commit = () => {
    setOpen(false);
    if (draft !== value) onCommitText(draft);
  };

  const pick = (ex: Exercise) => {
    setOpen(false);
    setDraft(ex.name);
    onPick(ex);
    inputRef.current?.blur();
  };

  return (
    <div ref={wrapRef} className="al-ex-autocomplete">
      <input
        ref={inputRef}
        type="text"
        className="al-ex-autocomplete-input"
        value={draft}
        placeholder={placeholder}
        onFocus={() => {
          ensureLoaded();
          setOpen(true);
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          // Delay para que el click en una sugerencia se procese antes del blur.
          window.setTimeout(commit, 150);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(value);
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && matches[hi]) {
              pick(matches[hi]);
            } else {
              (e.target as HTMLInputElement).blur();
            }
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((i) => Math.min(i + 1, Math.max(0, matches.length - 1)));
            setOpen(true);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((i) => Math.max(0, i - 1));
          }
        }}
      />
      {open && matches.length > 0 && (
        <div className="al-ex-autocomplete-list" role="listbox">
          {matches.map((ex, i) => {
            const url = exerciseGifUrl(ex);
            return (
              <button
                key={ex.id}
                type="button"
                role="option"
                aria-selected={i === hi}
                className={"al-ex-autocomplete-item " + (i === hi ? "is-hi" : "")}
                onMouseDown={(e) => {
                  // mousedown (no click) — se ejecuta ANTES del blur del input.
                  e.preventDefault();
                  pick(ex);
                }}
                onMouseEnter={() => setHi(i)}
              >
                <span className="al-ex-autocomplete-thumb">
                  {url ? <img src={url} alt="" loading="lazy" /> : null}
                </span>
                <span className="al-ex-autocomplete-name">
                  {ex.name}
                  {ex.name_en && normalize(ex.name).indexOf(normalize(draft)) < 0 && (
                    <span className="al-ex-autocomplete-alias"> · {ex.name_en}</span>
                  )}
                </span>
                <span className="al-ex-autocomplete-meta">{ex.muscle}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
